import { useMemo, useState } from "react";
import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  GitBranch, FileText, Clock, Armchair, Stethoscope,
  Users, Shield, Mail, RotateCcw, UserPlus,
  QrCode, Wallet, ShieldCheck, Gift, Sparkles,
  User, CreditCard, HelpCircle, Map, Search,
  Settings2, ChevronRight,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { canAccessAdminSettings } from "@/lib/settingsAccess";

const GROUPS = [
  {
    label: "Clinic setup",
    description: "The basics your team uses every day.",
    items: [
      { to: "/settings/branches", label: "Branches", desc: "Locations, branch status, and branch access.", icon: GitBranch, tid: "settings-nav-branches", access: "admin" },
      { to: "/settings/clinic", label: "Clinic details", desc: "Clinic name, contact details, documents, and treatment-plan terms.", icon: FileText, tid: "settings-nav-clinic", access: "owner_frontdesk" },
      { to: "/settings/appointments", label: "Hours & booking", desc: "Clinic hours, appointment defaults, walk-ins, and chair setup time.", icon: Clock, tid: "settings-nav-appointments" },
      { to: "/settings/chairs", label: "Chairs", desc: "Treatment chairs available for booking at each branch.", icon: Armchair, tid: "settings-nav-chairs" },
      { to: "/settings/services", label: "Services", desc: "Prices, durations, and treatment behavior for your clinic services.", icon: Stethoscope, tid: "settings-nav-services" },
    ],
  },
  {
    label: "Staff & access",
    description: "Who works in the clinic and what each role can use.",
    items: [
      { to: "/settings/team", label: "Staff", desc: "Invite staff, assign roles, branches, and doctor commission rates.", icon: Users, tid: "settings-nav-team" },
      { to: "/settings/permissions", label: "Permissions", desc: "Choose which sections and actions each clinic role can access.", icon: Shield, tid: "settings-nav-permissions" },
    ],
  },
  {
    label: "Patient communication",
    description: "What patients receive and how they enter your clinic.",
    items: [
      { to: "/settings/email-templates", label: "Email templates", desc: "Review the wording used in patient emails.", icon: Mail, tid: "settings-nav-email-templates" },
      { to: "/settings/recall", label: "Recall rules", desc: "Control when patients enter Recall and how follow-up attempts progress.", icon: RotateCcw, tid: "settings-nav-recall" },
      { to: "/settings/self-registration", label: "Patient sign-up", desc: "Share or print the clinic's patient registration link and QR code.", icon: UserPlus, tid: "settings-nav-self-registration", access: "owner_frontdesk" },
    ],
  },
  {
    label: "Payments",
    description: "Payment choices and insurance partners used at checkout.",
    items: [
      { to: "/settings/qr-codes", label: "Payment QR codes", desc: "GCash and bank QR codes shown during checkout.", icon: QrCode, tid: "settings-nav-qr-codes", access: "admin" },
      { to: "/settings/methods", label: "Payment methods", desc: "Choose which payment methods the clinic accepts.", icon: Wallet, tid: "settings-nav-methods", access: "admin" },
      { to: "/settings/insurance", label: "Insurance payers", desc: "HMOs and insurance partners accepted by the clinic.", icon: ShieldCheck, tid: "settings-nav-insurance", access: "owner_frontdesk" },
    ],
  },
  {
    label: "Clinical programs",
    description: "Clinic-wide rules for specialized treatment programs.",
    items: [
      { to: "/settings/ortho", label: "Ortho setup", desc: "Case rules, systems, wires, billing, and retention defaults.", icon: Sparkles, tid: "settings-nav-ortho" },
    ],
  },
  {
    label: "Account",
    description: "Your profile, subscription, and Dental OS account.",
    items: [
      { to: "/settings/profile", label: "My Profile", desc: "Your name, security, and sign-in settings.", icon: User, tid: "settings-nav-profile", access: "self" },
      { to: "/settings/billing", label: "Subscription & billing", desc: "Plan, trial, invoices, and Dental OS subscription details.", icon: CreditCard, tid: "settings-nav-billing", access: "admin" },
      { to: "/settings/referrals", label: "Referrals", desc: "Your clinic referral link and rewards.", icon: Gift, tid: "settings-nav-referrals" },
    ],
  },
  {
    label: "Help",
    description: "Guided help for setting up and learning Dental OS.",
    items: [
      { to: "/settings/setup-guide", label: "Setup guide", desc: "Finish the essential clinic setup steps.", icon: HelpCircle, tid: "settings-nav-setup" },
      { to: "/settings/tutorials", label: "Tutorials", desc: "Practice the main Dental OS workflows.", icon: Map, tid: "settings-nav-tutorials", access: "self" },
    ],
  },
];

function visibleGroupsForRole({ clinicRole, isPlatformAdmin }) {
  const canManageSettings = canAccessAdminSettings(clinicRole, isPlatformAdmin);
  return GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => canManageSettings || item.access === "self"),
  })).filter((group) => group.items.length > 0);
}

function isSelfServiceSettingsPath(pathname) {
  const normalizedPath = pathname.replace(/\/+$/, "") || "/";
  return ["/settings/profile", "/settings/tutorials"].includes(normalizedPath);
}

export default function Settings() {
  const location = useLocation();
  const { clinicRole, isPlatformAdmin } = useAuth();
  const [query, setQuery] = useState("");
  const groups = useMemo(
    () => visibleGroupsForRole({ clinicRole, isPlatformAdmin }),
    [clinicRole, isPlatformAdmin],
  );
  const canManageSettings = canAccessAdminSettings(clinicRole, isPlatformAdmin);
  const isOverview = location.pathname === "/settings" || location.pathname === "/settings/";
  const searchResults = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return [];
    return groups.flatMap((group) => group.items)
      .filter((item) => [item.label, item.desc].some((value) => value.toLowerCase().includes(needle)))
      .slice(0, 8);
  }, [groups, query]);

  if (!canManageSettings && !isSelfServiceSettingsPath(location.pathname)) {
    return <Navigate to="/settings/profile" replace />;
  }

  return (
    <div className="space-y-5" data-testid="settings-shell">
      <header className="rounded-2xl border border-slate-200 bg-white px-5 py-5 shadow-sm sm:px-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-950">Settings</h1>
            <p className="mt-1 text-sm text-slate-500">Set up how your clinic works. Changes apply only to this clinic.</p>
          </div>
          <div className="relative w-full lg:max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Find a setting"
              aria-label="Find a setting"
              data-testid="settings-search"
              className="h-11 w-full rounded-xl border border-slate-300 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none transition focus:border-teal-600 focus:ring-2 focus:ring-teal-100"
            />
            {query.trim() ? (
              <div className="absolute right-0 top-12 z-30 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl" data-testid="settings-search-results">
                {searchResults.length ? searchResults.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setQuery("")}
                      className="flex min-h-14 items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0 hover:bg-slate-50 focus:bg-slate-50 focus:outline-none"
                    >
                      <Icon className="h-4 w-4 shrink-0 text-slate-500" />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-slate-900">{item.label}</span>
                        <span className="block truncate text-xs text-slate-500">{item.desc}</span>
                      </span>
                    </NavLink>
                  );
                }) : (
                  <p className="px-4 py-5 text-sm text-slate-500">No matching setting.</p>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </header>

      <div className="flex flex-col gap-5 md:flex-row md:items-start md:gap-7">
        <aside className="md:w-60 md:shrink-0">
          <nav className="max-h-[calc(100vh-3rem)] overflow-y-auto rounded-xl border border-slate-200 bg-white p-2 shadow-sm md:sticky md:top-6">
            {canManageSettings ? (
              <NavLink
                to="/settings"
                end
                data-testid="settings-nav-overview"
                className={({ isActive }) => `mb-3 flex min-h-10 items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition ${isActive ? "bg-teal-50 text-teal-800" : "text-slate-700 hover:bg-slate-50"}`}
              >
                <Settings2 className="h-4 w-4" />
                Overview
              </NavLink>
            ) : null}
            {groups.map((group, groupIndex) => (
              <div key={group.label} className={groupIndex > 0 ? "mt-4" : ""}>
                <p className="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{group.label}</p>
                {group.items.map(({ to, label, icon: Icon, tid }) => (
                  <NavLink
                    key={to}
                    to={to}
                    data-testid={tid}
                    data-tutorial-id={tid}
                    className={({ isActive }) => `flex min-h-10 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition ${isActive ? "bg-teal-50 text-teal-800" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}
                  >
                    <Icon className="h-4 w-4 shrink-0" strokeWidth={2} />
                    <span>{label}</span>
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>
        </aside>

        <section className="min-w-0 flex-1">
          {isOverview ? <SettingsOverview groups={groups} /> : <Outlet />}
        </section>
      </div>
    </div>
  );
}

function SettingsOverview({ groups }) {
  return (
    <div className="space-y-5" data-testid="settings-overview" data-tutorial-id="settings-overview">
      <div>
        <h2 className="text-xl font-bold text-slate-950">Clinic settings</h2>
        <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500">Choose what you want to change. Most clinics only need the first three sections during setup.</p>
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        {groups.map((group) => (
          <section key={group.label} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-5 py-4">
              <h3 className="text-sm font-bold text-slate-950">{group.label}</h3>
              <p className="mt-1 text-xs leading-5 text-slate-500">{group.description}</p>
            </div>
            <div className="divide-y divide-slate-100">
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink key={item.to} to={item.to} className="group flex min-h-[68px] items-center gap-3 px-5 py-3 hover:bg-slate-50 focus:bg-slate-50 focus:outline-none">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600 group-hover:bg-teal-50 group-hover:text-teal-700">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-slate-900">{item.label}</span>
                      <span className="mt-0.5 block text-xs leading-5 text-slate-500">{item.desc}</span>
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 group-hover:text-teal-600" />
                  </NavLink>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
